// The "Access" tab of the group share dialog — the people half of sharing.
//
// Lists every member of the group with their role, admin or viewer. It is
// only ever shown to an admin (the dialog checks `can_manage`), who may invite
// people — always as viewers — and remove viewers. Admins are made and removed
// in app.divako.io only, so an admin's row has no actions here. Everything
// here is enforced server-side too — this component never decides who may do
// what.

import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  avatarUrl,
  grantGroupAccess,
  isAdmin,
  listGroupAccess,
  revokeGroupAccess,
  type AccessPerson,
  type AccessRole,
} from '../lib/api';
import { formatDate } from '../lib/meterCharts';
import type { Wording } from '../lib/projectType';
import Icon from './Icon';
import { Spinner, toast } from './ui';

// The person's profile picture, from the same public endpoint the topbar uses.
// It answers for every uid — a generic silhouette when the account has never
// uploaded one — so the initial below is a fallback for a failed request, not
// for a missing picture.
function AccessAvatar({ person }: { person: AccessPerson }): ReactElement {
  const [broken, setBroken] = useState(false);
  const initial = (person.name || person.email).trim().charAt(0).toUpperCase();

  if (broken) {
    return (
      <span className="access-avatar" aria-hidden="true">
        {initial}
      </span>
    );
  }
  return (
    <img
      className="access-avatar"
      src={avatarUrl(person.uid)}
      alt=""
      onError={() => setBroken(true)}
    />
  );
}

export default function GroupAccessPanel({
  gid,
  wording,
}: {
  gid: string;
  /** How the group's project words it: an apartment in Sub-metering. */
  wording: Wording;
}): ReactElement {
  const { t } = useTranslation();

  const [people, setPeople] = useState<AccessPerson[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The viewer a removal confirmation is open for.
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoadFailed(false);
    listGroupAccess(gid)
      .then((r) => setPeople(r.people))
      .catch(() => setLoadFailed(true));
  }, [gid]);

  useEffect(() => load(), [load]);

  const showDate = (iso?: string | null): string =>
    iso ? formatDate(`${iso}T00:00:00`) : '';

  // 'en-CA' formats as YYYY-MM-DD, which showDate then renders in the user's
  // locale. This is only the label — the authoritative start date is the one
  // the backend computes from the account's stored timezone and returns as
  // `since` on the row that appears after inviting.
  const today = showDate(new Date().toLocaleDateString('en-CA'));

  // Both fields are required: the name is what the group's admins see in the
  // access list, and what the invitee's new account is created with.
  const canSubmit = !busy && email.trim() !== '' && name.trim() !== '';

  const submit = (): void => {
    setBusy(true);
    setError(null);
    grantGroupAccess(gid, email.trim(), name.trim())
      .then((r) => {
        toast(
          r.created
            ? t('access.invited', { email: r.email })
            : t('access.grantedExisting', { name: r.name || r.email, context: wording }),
        );
        setEmail('');
        setName('');
        load();
      })
      .catch((e: unknown) => {
        const status = e instanceof ApiError ? e.status : 0;
        setError(
          status === 409
            ? t('access.alreadyHasAccess', { context: wording })
            : status === 429
              ? t('access.limitReached', { context: wording })
              : status === 400
                ? t('access.invalidEmail')
                : t('access.grantFailed'),
        );
      })
      .finally(() => setBusy(false));
  };

  const revoke = (uid: string): void => {
    setConfirming(null);
    setBusy(true);
    revokeGroupAccess(gid, uid)
      .then(() => {
        toast(t('access.removed'));
        load();
      })
      .catch(() => setError(t('access.removeFailed')))
      .finally(() => setBusy(false));
  };

  // The signed-in user first, then everyone else in the backend's order.
  const rows = [...(people ?? [])].sort((a, b) => Number(b.is_me) - Number(a.is_me));
  const roleLabel = (role: AccessRole): string =>
    t(isAdmin(role) ? 'access.roleOwner' : 'access.roleViewer');

  return (
    <>
      <div className="share-section">
        <div className="share-label">
          <Icon name="user" size={15} />
          {t('access.listTitle')}
        </div>

        {people === null && !loadFailed && <Spinner />}
        {loadFailed && <p className="share-note">{t('access.loadFailed')}</p>}

        {people !== null && (
          <ul className="access-list">
            {rows.map((p) => (
              <li
                key={p.uid}
                className={`access-row${confirming === p.uid ? ' is-confirming' : ''}`}
              >
                <AccessAvatar person={p} />
                <div className="access-who">
                  <strong>
                    {p.name || p.email}
                    {p.is_me && <span className="access-tag">{t('access.you')}</span>}
                    <span className={`access-role${isAdmin(p.role) ? ' is-admin' : ''}`}>
                      {roleLabel(p.role)}
                    </span>
                  </strong>
                  <span className="access-meta">
                    {p.email}
                    {p.since ? ` · ${t('access.since', { date: showDate(p.since) })}` : ''}
                  </span>
                </div>
                {p.is_me || isAdmin(p.role) ? null : confirming === p.uid ? (
                  <div className="access-confirm">
                    <p className="share-note">
                      {t('access.removeConfirm', { name: p.name || p.email, context: wording })}
                    </p>
                    <div className="share-confirm-actions">
                      <button
                        className="btn btn-danger"
                        disabled={busy}
                        onClick={() => revoke(p.uid)}
                      >
                        {t('access.removeConfirmYes')}
                      </button>
                      <button className="btn btn-ghost" onClick={() => setConfirming(null)}>
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="access-actions">
                    <button
                      className="btn btn-ghost access-remove"
                      disabled={busy}
                      onClick={() => setConfirming(p.uid)}
                    >
                      {t('access.remove')}
                    </button>
                  </div>
                )}
              </li>
            ))}

            {rows.length <= 1 && <li className="access-empty">{t('access.empty', { context: wording })}</li>}
          </ul>
        )}
      </div>

      <div className="share-section access-invite">
        <div className="share-label">
          <Icon name="share" size={15} />
          {t('access.inviteTitle')}
        </div>

        {/* Enter submits from either field, the way a one-line form should. */}
        <form
          className="access-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) submit();
          }}
        >
          <div className="field">
            <label className="field-label" htmlFor="access-email">
              {t('access.emailLabel')}
            </label>
            <input
              id="access-email"
              type="email"
              className="input"
              autoComplete="email"
              required
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="access-name">
              {t('access.nameLabel')}
            </label>
            <input
              id="access-name"
              type="text"
              className="input"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="access-form-foot">
            <p className="access-hint">
              {t('access.startsToday', { date: today })} {t('access.joinsAsViewer', { context: wording })}
            </p>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {busy ? <Spinner /> : t('access.submit')}
            </button>
          </div>
        </form>

        {error && (
          <p className="form-error" role="alert">
            <Icon name="alert-triangle" size={15} />
            {error}
          </p>
        )}
      </div>
    </>
  );
}
